import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  People as PeopleIcon,
  Star as StarIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';
import merchantService from '../services/merchant.service';
import LoadingAnimation from '../components/LoadingAnimation';

export default function MerchantNotifications() {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [segments, setSegments] = useState<any>(null);
  const [pincodes, setPincodes] = useState<string[]>([]);
  const [pincodeGroups, setPincodeGroups] = useState<any>({});
  const [allPincodes, setAllPincodes] = useState<string[]>([]);
  const [allPincodeGroups, setAllPincodeGroups] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    segment: 'all',
    pincode: 'all',
  });

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const data = await merchantService.getCustomerSegments();
      setSegments(data.segments);
      setPincodes(data.pincodes || []);
      setPincodeGroups(data.pincodeGroups || {});
      setAllPincodes(data.allPincodes || []);
      setAllPincodeGroups(data.allPincodeGroups || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching segments:', error);
      setErrorMessage('Failed to load customer segments');
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      if (!formData.title.trim() || !formData.message.trim()) {
        setErrorMessage('Please enter both title and message');
        return;
      }

      setSending(true);
      const result = await merchantService.sendNotification(formData);

      setSuccessMessage(result.message || `Notification sent to ${result.sentCount} customers!`);
      setFormData({ title: '', message: '', segment: 'all', pincode: 'all' });

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Error sending notification:', error);
      const errMsg = error.response?.data?.error || error.message || 'Failed to send notification';
      setErrorMessage(errMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSending(false);
    }
  };

  // Calculate filtered count based on segment and pincode
  const getFilteredCount = () => {
    let count = 0;

    // Start with segment count
    if (formData.segment === 'everyone') count = segments?.everyone || 0;
    else if (formData.segment === 'all') count = segments?.all || 0;
    else if (formData.segment === 'loyal') count = segments?.loyal || 0;
    else if (formData.segment === 'regular') count = segments?.regular || 0;
    else if (formData.segment === 'new') count = segments?.new || 0;

    // If pincode filter is applied, adjust count
    if (formData.pincode !== 'all') {
      const groups = formData.segment === 'everyone' ? allPincodeGroups : pincodeGroups;
      count = groups[formData.pincode] || 0;
    }

    return count;
  };

  if (loading) {
    return <LoadingAnimation message="Loading notification center..." subtitle="Preparing your messaging tools" />;
  }

  const segmentOptions = [
    {
      value: 'everyone',
      label: 'Everyone',
      icon: <PeopleIcon />,
      count: segments?.everyone || 0,
      description: 'All customers (even those who haven\'t ordered)',
      color: 'secondary',
    },
    {
      value: 'all',
      label: 'All Customers',
      icon: <PeopleIcon />,
      count: segments?.all || 0,
      description: 'Send to all customers who have ordered from you',
      color: 'primary',
    },
    {
      value: 'loyal',
      label: 'Loyal Customers',
      icon: <StarIcon />,
      count: segments?.loyal || 0,
      description: 'Customers with 10+ orders',
      color: 'warning',
    },
    {
      value: 'regular',
      label: 'Regular Customers',
      icon: <PeopleIcon />,
      count: segments?.regular || 0,
      description: 'Customers with 5-9 orders',
      color: 'info',
    },
    {
      value: 'new',
      label: 'New Customers',
      icon: <NewIcon />,
      count: segments?.new || 0,
      description: 'First order within last 30 days',
      color: 'success',
    },
  ];

  const selectedSegment = segmentOptions.find(s => s.value === formData.segment);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Send Notifications
        </Typography>
        <Typography color="text.secondary">
          Engage with your customers through targeted notifications
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Customer Segments Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        {segmentOptions.map((segment) => (
          <Paper
            key={segment.value}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              border: formData.segment === segment.value ? `2px solid` : 'none',
              borderColor: `${segment.color}.main`,
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
            onClick={() => setFormData({ ...formData, segment: segment.value })}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              {React.cloneElement(segment.icon, { sx: { color: `${segment.color}.main`, fontSize: 32 } })}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {segment.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: `${segment.color}.main` }}>
              {segment.count}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Notification Form */}
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Compose Notification
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Target Audience</InputLabel>
                  <Select
                    value={formData.segment}
                    label="Target Audience"
                    onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                  >
                    {segmentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          <Box>
                            <Typography>{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description} ({option.count} customers)
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Pincode Filter</InputLabel>
                  <Select
                    value={formData.pincode}
                    label="Pincode Filter"
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  >
                    <MenuItem value="all">
                      <Box>
                        <Typography>All Locations</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Send to all pincodes
                        </Typography>
                      </Box>
                    </MenuItem>
                    {(formData.segment === 'everyone' ? allPincodes : pincodes).map((pincode) => (
                      <MenuItem key={pincode} value={pincode}>
                        <Box>
                          <Typography>Pincode: {pincode}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(formData.segment === 'everyone' ? allPincodeGroups : pincodeGroups)[pincode] || 0} customers
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label="Notification Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., New Boxes Available!"
                sx={{ mb: 2 }}
                slotProps={{ htmlInput: { maxLength: 60 } }}
                helperText={`${formData.title.length}/60 characters`}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="e.g., Check out our fresh surprise boxes for today. Limited quantities available!"
                slotProps={{ htmlInput: { maxLength: 200 } }}
                helperText={`${formData.message.length}/200 characters`}
              />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Preview
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {formData.title || 'Your Title Here'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.message || 'Your message will appear here...'}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={selectedSegment?.icon}
                    label={`To: ${selectedSegment?.label}`}
                    color={selectedSegment?.color as any}
                  />
                  {formData.pincode !== 'all' && (
                    <Chip
                      size="small"
                      label={`Pincode: ${formData.pincode}`}
                      color="secondary"
                    />
                  )}
                  <Chip size="small" label={`${getFilteredCount()} recipients`} />
                </Box>
              </Paper>
            </Box>

            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Notification Tips:
              </Typography>
              <Typography variant="body2">
                • Keep messages clear and concise<br />
                • Include a call-to-action<br />
                • Mention special offers or time-sensitive deals<br />
                • Be respectful of customer preferences
              </Typography>
            </Alert>

            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={sending || !formData.title.trim() || !formData.message.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              {sending ? 'Sending...' : `Send to ${getFilteredCount()} Customers`}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
